import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import '../App.css';

export const PrivacyPage = () => {
    return (
        <div className="legal-page-container">
            <div className="legal-page-content">
                <Link to="/" className="back-link">
                    <ArrowLeft size={20} />
                    <span>Back to Home</span>
                </Link>

                <h1>Privacy Policy</h1>
                <p className="last-updated">Last Updated: November 25, 2025</p>

                <section>
                    <h2>1. Introduction</h2>
                    <p>
                        The Trend Junkies ("we," "our," or "us") operates CutIQ, a video downloading and editing platform.
                        This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use
                        our Service.
                    </p>
                    <p>
                        By using CutIQ, you agree to the collection and use of information in accordance with this policy.
                        If you do not agree with our policies and practices, please do not use the Service.
                    </p>
                </section>

                <section>
                    <h2>2. Information We Collect</h2>

                    <h3>2.1 Account Information</h3>
                    <p>When you create an account, we collect:</p>
                    <ul>
                        <li><strong>Email address:</strong> Used for authentication and communication</li>
                        <li><strong>Password:</strong> Securely hashed and stored (we never see your plain-text password)</li>
                        <li><strong>Display name:</strong> Derived from your email or provided by you</li>
                        <li><strong>OAuth data:</strong> If you sign in with Google, we receive your email and profile information</li>
                    </ul>

                    <h3>2.2 Usage Data</h3>
                    <p>We automatically collect information about your use of the Service:</p>
                    <ul>
                        <li><strong>Download history:</strong> URLs, titles, formats, and timestamps of your downloads</li>
                        <li><strong>Usage statistics:</strong> Number of video/audio downloads per billing cycle</li>
                        <li><strong>Edit history:</strong> Records of video editing operations (Unlimited tier only)</li>
                        <li><strong>Device information:</strong> Browser type, operating system, IP address</li>
                        <li><strong>Session data:</strong> Login times, feature usage patterns</li>
                    </ul>

                    <h3>2.3 Payment Information</h3>
                    <p>
                        Payment processing is handled securely by <strong>Stripe</strong>. We do not store your full credit card
                        information. We receive from Stripe:
                    </p>
                    <ul>
                        <li>Last 4 digits of your card</li>
                        <li>Card brand (Visa, Mastercard, etc.)</li>
                        <li>Subscription status and billing history</li>
                        <li>Customer ID for managing your subscription</li>
                    </ul>

                    <h3>2.4 Cookies and Tracking</h3>
                    <p>We use the following types of cookies:</p>
                    <ul>
                        <li><strong>Essential cookies:</strong> Required for authentication and core functionality</li>
                        <li><strong>Preference cookies:</strong> Remember your settings and preferences</li>
                        <li><strong>Analytics cookies:</strong> Help us understand how users interact with the Service</li>
                    </ul>
                </section>

                <section>
                    <h2>3. How We Use Your Information</h2>
                    <p>We use collected information for the following purposes:</p>

                    <h3>3.1 Service Delivery</h3>
                    <ul>
                        <li>Authenticate your account and verify your identity</li>
                        <li>Process video downloads and editing requests</li>
                        <li>Enforce usage limits based on your subscription tier</li>
                        <li>Apply or remove watermarks as appropriate</li>
                        <li>Maintain your download and edit history</li>
                    </ul>

                    <h3>3.2 Billing and Subscriptions</h3>
                    <ul>
                        <li>Process subscription payments through Stripe</li>
                        <li>Track your billing cycle and usage quotas</li>
                        <li>Send billing notifications and receipts</li>
                        <li>Manage subscription upgrades, downgrades, and cancellations</li>
                    </ul>

                    <h3>3.3 Communication</h3>
                    <ul>
                        <li>Send important service updates and announcements</li>
                        <li>Respond to your support requests and inquiries</li>
                        <li>Notify you about changes to our Terms or Privacy Policy</li>
                        <li>Send promotional emails (you can opt out anytime)</li>
                    </ul>

                    <h3>3.4 Improvement and Analytics</h3>
                    <ul>
                        <li>Analyze usage patterns to improve the Service</li>
                        <li>Identify and fix technical issues</li>
                        <li>Develop new features based on user needs</li>
                        <li>Monitor performance and optimize infrastructure</li>
                    </ul>

                    <h3>3.5 Security and Compliance</h3>
                    <ul>
                        <li>Detect and prevent fraud or abuse</li>
                        <li>Enforce our Terms of Service</li>
                        <li>Comply with legal obligations</li>
                        <li>Protect our rights and the rights of other users</li>
                    </ul>
                </section>

                <section>
                    <h2>4. Third-Party Services</h2>
                    <p>
                        We use the following third-party services to operate CutIQ. Each has its own privacy policy:
                    </p>

                    <h3>4.1 Supabase (Authentication & Database)</h3>
                    <ul>
                        <li><strong>Purpose:</strong> User authentication and data storage</li>
                        <li><strong>Data shared:</strong> Email, password hash, usage statistics, download history</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer">supabase.com/privacy</a></li>
                        <li><strong>Location:</strong> Data stored in Supabase's cloud infrastructure (AWS)</li>
                    </ul>

                    <h3>4.2 Stripe (Payment Processing)</h3>
                    <ul>
                        <li><strong>Purpose:</strong> Secure payment processing and subscription management</li>
                        <li><strong>Data shared:</strong> Email, payment information, billing history</li>
                        <li><strong>Privacy Policy:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">stripe.com/privacy</a></li>
                        <li><strong>Security:</strong> PCI DSS Level 1 certified</li>
                    </ul>

                    <h3>4.3 yt-dlp (Download Engine)</h3>
                    <ul>
                        <li><strong>Purpose:</strong> Download videos from supported platforms</li>
                        <li><strong>Data shared:</strong> Video URLs you request to download</li>
                        <li><strong>Note:</strong> yt-dlp is open-source and runs on our servers; no data sent to third parties</li>
                    </ul>

                    <h3>4.4 YouTube API Services</h3>
                    <ul>
                        <li><strong>Service:</strong> We use YouTube API Services to enable video uploads and channel management</li>
                        <li><strong>Data accessed:</strong> Channel profile information and video upload capabilities</li>
                        <li><strong>Google Privacy Policy:</strong> <a href="http://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer">http://www.google.com/policies/privacy</a></li>
                        <li><strong>Revocation:</strong> You can revoke our access anytime via the <a href="https://security.google.com/settings/security/permissions" target="_blank" rel="noopener noreferrer">Google Security Settings</a> page</li>
                        <li><strong>Compliance:</strong> Our use of information received from Google APIs will adhere to the <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Data Storage and Security</h2>

                    <h3>5.1 Where We Store Data</h3>
                    <ul>
                        <li><strong>User data:</strong> Stored in Supabase's PostgreSQL database (encrypted at rest)</li>
                        <li><strong>Downloaded files:</strong> Temporarily stored on our servers, deleted within 1 hour</li>
                        <li><strong>Edited files:</strong> Temporarily stored, automatically cleaned up after 1 hour</li>
                        <li><strong>Backups:</strong> Regular database backups maintained by Supabase</li>
                    </ul>

                    <h3>5.2 Security Measures</h3>
                    <p>We implement industry-standard security practices:</p>
                    <ul>
                        <li><strong>Encryption:</strong> HTTPS/TLS for all data in transit</li>
                        <li><strong>Password security:</strong> Bcrypt hashing with salt</li>
                        <li><strong>Authentication:</strong> JWT tokens with expiration</li>
                        <li><strong>Database security:</strong> Row Level Security (RLS) policies in Supabase</li>
                        <li><strong>Payment security:</strong> PCI compliance through Stripe</li>
                        <li><strong>Access control:</strong> Limited employee access to user data</li>
                    </ul>

                    <h3>5.3 Data Retention</h3>
                    <ul>
                        <li><strong>Active accounts:</strong> Data retained as long as your account is active</li>
                        <li><strong>Deleted accounts:</strong> Data permanently deleted within 30 days</li>
                        <li><strong>Download history:</strong> Kept for the duration of your account</li>
                        <li><strong>Billing records:</strong> Retained for 7 years for tax and legal compliance</li>
                        <li><strong>Temporary files:</strong> Downloaded/edited videos deleted after 1 hour</li>
                    </ul>
                </section>

                <section>
                    <h2>6. Your Privacy Rights</h2>

                    <h3>6.1 Access and Portability</h3>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Access all personal data we have about you</li>
                        <li>Download your data in a portable format</li>
                        <li>Request a copy of your download history</li>
                    </ul>

                    <h3>6.2 Correction and Update</h3>
                    <p>You can:</p>
                    <ul>
                        <li>Update your email and profile information in Settings</li>
                        <li>Change your password anytime</li>
                        <li>Request correction of inaccurate data by contacting support</li>
                    </ul>

                    <h3>6.3 Deletion</h3>
                    <p>You have the right to:</p>
                    <ul>
                        <li>Delete your account through Settings → Account → Delete Account</li>
                        <li>Request deletion of specific data by contacting support</li>
                        <li>Opt out of promotional emails (unsubscribe links provided)</li>
                    </ul>
                    <p>
                        <strong>Note:</strong> Some data may be retained for legal or legitimate business purposes even after
                        account deletion (e.g., billing records, fraud prevention).
                    </p>

                    <h3>6.4 Objection and Restriction</h3>
                    <p>You can:</p>
                    <ul>
                        <li>Object to processing of your data for marketing purposes</li>
                        <li>Request restriction of data processing in certain circumstances</li>
                        <li>Withdraw consent where processing is based on consent</li>
                    </ul>
                </section>

                <section>
                    <h2>7. International Data Transfers</h2>
                    <p>
                        Your information may be transferred to and maintained on servers located outside your jurisdiction.
                        We ensure appropriate safeguards are in place:
                    </p>
                    <ul>
                        <li>Supabase infrastructure is AWS-based with global data centers</li>
                        <li>Data transfers comply with GDPR requirements</li>
                        <li>Standard contractual clauses used where applicable</li>
                    </ul>
                </section>

                <section>
                    <h2>8. Children's Privacy</h2>
                    <p>
                        CutIQ is not intended for users under 13 years of age. We do not knowingly collect personal
                        information from children under 13. If you are a parent or guardian and believe your child has
                        provided us with personal information, please contact us immediately.
                    </p>
                </section>

                <section>
                    <h2>9. California Privacy Rights (CCPA)</h2>
                    <p>If you are a California resident, you have additional rights:</p>
                    <ul>
                        <li><strong>Right to Know:</strong> What personal information we collect and how we use it</li>
                        <li><strong>Right to Delete:</strong> Request deletion of your personal information</li>
                        <li><strong>Right to Opt-Out:</strong> Opt out of sale of personal information (we do not sell data)</li>
                        <li><strong>Right to Non-Discrimination:</strong> Equal service regardless of privacy choices</li>
                    </ul>
                    <p>
                        To exercise these rights, email us at support@cutiq.io with "CCPA Request" in the subject line.
                    </p>
                </section>

                <section>
                    <h2>10. European Privacy Rights (GDPR)</h2>
                    <p>If you are in the European Economic Area (EEA), you have rights under GDPR:</p>
                    <ul>
                        <li><strong>Legal basis:</strong> We process data based on contract performance, consent, and legitimate interests</li>
                        <li><strong>Data controller:</strong> The Trend Junkies is the data controller</li>
                        <li><strong>Right to lodge complaint:</strong> You can file a complaint with your local data protection authority</li>
                        <li><strong>Data transfers:</strong> We use appropriate safeguards for international transfers</li>
                    </ul>
                    <p>
                        To exercise your GDPR rights, contact us at support@cutiq.io with "GDPR Request" in the subject line.
                    </p>
                </section>

                <section>
                    <h2>11. Do Not Track Signals</h2>
                    <p>
                        Some browsers include a "Do Not Track" (DNT) feature. Currently, our Service does not respond to DNT
                        signals, as there is no industry-wide standard for how to interpret them.
                    </p>
                </section>

                <section>
                    <h2>12. Changes to This Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of material changes by:
                    </p>
                    <ul>
                        <li>Email notification to your registered email address</li>
                        <li>Prominent notice on our website or app</li>
                        <li>Updating the "Last Updated" date at the top of this policy</li>
                    </ul>
                    <p>
                        Your continued use of the Service after changes take effect constitutes acceptance of the updated policy.
                    </p>
                </section>

                <section>
                    <h2>13. Contact Us</h2>
                    <p>
                        If you have questions, concerns, or requests regarding this Privacy Policy or our data practices,
                        please contact us:
                    </p>
                    <ul>
                        <li><strong>Email:</strong> support@cutiq.io</li>
                        <li><strong>Privacy Officer:</strong> privacy@cutiq.io</li>
                        <li><strong>Company:</strong> The Trend Junkies</li>
                        <li><strong>Address:</strong> [Your Business Address]</li>
                    </ul>
                    <p>
                        <strong>Response Time:</strong> We aim to respond to all privacy inquiries within 30 days.
                    </p>
                </section>

                <section>
                    <h2>14. Data Processing Summary</h2>
                    <div className="data-summary-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Data Type</th>
                                    <th>Purpose</th>
                                    <th>Retention</th>
                                    <th>Third Party</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Email & Password</td>
                                    <td>Authentication</td>
                                    <td>Account lifetime</td>
                                    <td>Supabase</td>
                                </tr>
                                <tr>
                                    <td>Download History</td>
                                    <td>Service delivery, usage tracking</td>
                                    <td>Account lifetime</td>
                                    <td>Supabase</td>
                                </tr>
                                <tr>
                                    <td>Payment Info</td>
                                    <td>Billing</td>
                                    <td>7 years (legal requirement)</td>
                                    <td>Stripe</td>
                                </tr>
                                <tr>
                                    <td>Usage Statistics</td>
                                    <td>Quota enforcement, analytics</td>
                                    <td>Account lifetime</td>
                                    <td>Supabase</td>
                                </tr>
                                <tr>
                                    <td>Downloaded Videos</td>
                                    <td>Temporary processing</td>
                                    <td>1 hour (auto-delete)</td>
                                    <td>None</td>
                                </tr>
                                <tr>
                                    <td>Edited Videos</td>
                                    <td>Temporary processing</td>
                                    <td>1 hour (auto-delete)</td>
                                    <td>None</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <div className="legal-footer">
                    <p>
                        This Privacy Policy is designed to help you understand how we collect, use, and protect your personal
                        information. If you have any concerns, please don't hesitate to contact us.
                    </p>
                </div>
            </div>
        </div>
    );
};
